import { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router";
import { Alert, Box, Button, Collapse, Divider, Grid, Snackbar, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useAuth } from "src/context/AuthContext";
import { useLang, useT } from "src/i18n";
import { resolveErrorMessage } from "src/i18n/errorCopy";
import { useCurrentPerson } from "src/utils/useCurrentPerson";
import { getRequest, listOffers, acceptOffer, listDeclinedProviders, listViewers } from "src/utils/valut/valutApi";
import { listPumppuRequestAttachments } from "src/utils/quote/quoteAttachments";
import { captureError } from "src/utils/errorReporting";
import TilaaBackdrop from "src/components/quote/TilaaBackdrop";
import StepHeader from "src/components/quote/StepHeader";
import SpecRow from "src/components/quote/SpecRow";
import FrostedCard from "src/components/quote/FrostedCard";
import AttachmentsSection from "src/components/quote/AttachmentsSection";
import OfferProgressIndicator from "src/components/valut/OfferProgressIndicator";
import OfferCard from "src/components/valut/OfferCard";
import AcceptConfirmModal from "src/components/valut/AcceptConfirmModal";
import ValuAcceptedScreen from "src/components/valut/ValuAcceptedScreen";
import SubscribePushButton from "src/components/notifications/SubscribePushButton";
import { formatSentDate } from "src/utils/valut/valutFormatters";
import { amberCtaSx } from "src/components/quote/amberCtaSx";
import { kayttokohdeOptions, labelFor } from "src/utils/quote/quoteConstants";
import { OFFER_ACCEPTANCE_TYPE, gate2Fallback } from "src/utils/legal/acceptanceCopy";
import { useAcceptanceCopy } from "src/hooks/useAcceptanceCopy";

export default function ValuDetail() {
    const { id: idParam } = useParams();
    const id = Number(idParam);
    const { token, logout } = useAuth();
    const navigate = useNavigate();
    const { lang } = useLang();
    const t = useT();
    const kkOptions = useMemo(() => kayttokohdeOptions(t), [t]);

    const person = useCurrentPerson();
    const [request, setRequest] = useState(null);
    const [offers, setOffers] = useState(null);
    const [declinedProviders, setDeclinedProviders] = useState([]);
    const [viewers, setViewers] = useState([]);
    const [attachments, setAttachments] = useState([]);
    const [error, setError] = useState(null);
    // Memoized: useAcceptanceCopy's `fallback` param is a useEffect dependency
    // compared by reference, so a fresh object every render would refetch forever.
    const gate2FallbackCopy = useMemo(() => gate2Fallback(t), [t]);
    const { copy: acceptanceCopy } = useAcceptanceCopy(OFFER_ACCEPTANCE_TYPE, gate2FallbackCopy);

    const refetchAttachments = useCallback(async () => {
        if (!Number.isFinite(id) || !token) return;
        try {
            const list = await listPumppuRequestAttachments({ pumppuRequestId: id, token });
            setAttachments(Array.isArray(list) ? list : []);
        } catch (err) {
            if (err?.status === 401) { logout(); return; }
            // 403/404 = the valu isn't this person's (stale link, wrong account).
            // Expected, not a defect — fetchAll's 404 branch already renders the
            // "ei löydy" state, so reporting here would only be Sentry noise.
            if (err?.status === 403 || err?.status === 404) return;
            captureError(err, {
                tags: { entity: "attachment", operation: "list" },
                extra: { pumppuRequestId: id },
            });
        }
    }, [id, token, logout]);

    const theme = useTheme();
    const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
    const [recapOpen, setRecapOpen] = useState(isMdUp);

    const fetchAll = useCallback(async () => {
        try {
            const [req, off, declined, viewersRes] = await Promise.all([
                getRequest({ token, id }),
                listOffers({ token, requestId: id }),
                listDeclinedProviders({ token, requestId: id }).catch(() => []),
                listViewers({ token, requestId: id }).catch(() => []),
            ]);
            setRequest(req);
            setOffers(off);
            setDeclinedProviders(Array.isArray(declined) ? declined : []);
            setViewers(Array.isArray(viewersRes) ? viewersRes : []);
        } catch (err) {
            if (err.status === 401) { logout(); return; }
            if (err.status === 404) {
                setError(err);
                return;
            }
            captureError(err, {
                tags: { entity: "pumppuRequest", operation: "detail_get" },
                extra: { pumppuRequestId: id },
            });
            setError(err);
        }
    }, [token, id, logout]);

    useEffect(() => {
        if (!Number.isFinite(id)) {
            navigate("/valut", { replace: true });
            return;
        }
        async function run() {
            await fetchAll();
            refetchAttachments();
        }
        run();
    }, [id, fetchAll, navigate, refetchAttachments]);

    useEffect(() => {
        if (!request) return;
        document.title = t("valut.detail.pageTitle", { address: request.formattedAddress || request.osoite });
        return () => { document.title = t("valut.detail.pageTitleFallback"); };
    }, [request, t]);

    // Foreground push handler. The SW posts FCM_MESSAGE_RECEIVED to the focused
    // tab instead of showing an OS notification; we refresh offers and toast.
    const [pushToast, setPushToast] = useState(null);
    useEffect(() => {
        if (typeof navigator === "undefined" || !navigator.serviceWorker) return;
        const onMessage = (event) => {
            const msg = event.data;
            if (!msg || msg.type !== "FCM_MESSAGE_RECEIVED") return;
            const targetId = Number(msg.payload?.data?.pumppuRequestId);
            if (Number.isFinite(targetId) && targetId !== id) return;
            const title = msg.payload?.notification?.title || msg.payload?.data?.title || t("valut.detail.pushToastDefaultTitle");
            setPushToast(title);
            fetchAll();
        };
        navigator.serviceWorker.addEventListener("message", onMessage);
        return () => navigator.serviceWorker.removeEventListener("message", onMessage);
    }, [id, fetchAll, t]);

    const [pendingOffer, setPendingOffer] = useState(null);
    const [accepting, setAccepting] = useState(false);
    const [acceptError, setAcceptError] = useState(null);

    async function handleConfirmAccept() {
        if (!pendingOffer) return;
        setAccepting(true);
        setAcceptError(null);
        try {
            await acceptOffer({ token, requestId: id, offerId: pendingOffer.pumppuOfferId });
            setPendingOffer(null);
            await fetchAll();
        } catch (err) {
            captureError(err, {
                tags: { entity: "pumppuOffer", operation: "accept" },
                extra: { pumppuRequestId: id, pumppuOfferId: pendingOffer.pumppuOfferId },
            });
            setAcceptError(resolveErrorMessage(err, { t, lang, fallbackKey: "offers.acceptFailed" }));
        } finally {
            setAccepting(false);
        }
    }

    if (error?.status === 404) {
        return (
            <TilaaBackdrop>
                <Typography variant="body1">{t("valut.detail.notFound")}</Typography>
                <Button component={RouterLink} to="/valut" sx={{ mt: 2 }}>
                    {t("valut.detail.backToMyValut")}
                </Button>
            </TilaaBackdrop>
        );
    }

    if (!request) {
        return (
            <TilaaBackdrop>
                <Typography variant="body2">{t("common.loading")}</Typography>
            </TilaaBackdrop>
        );
    }

    if (request.status === "accepted") {
        const acceptedOffer = (offers || []).find((o) => o.status === "accepted") || null;
        return (
            <TilaaBackdrop>
                <ValuAcceptedScreen acceptedOffer={acceptedOffer} copy={acceptanceCopy} />
            </TilaaBackdrop>
        );
    }

    return (
        <TilaaBackdrop>
            <Button component={RouterLink} to="/valut" size="small" sx={{ mb: 2 }}>
                {t("valut.detail.backToMyValut")}
            </Button>

            <StepHeader>{request.formattedAddress || request.osoite}</StepHeader>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
                {[
                    labelFor(kkOptions, request.kayttokohde, request.kayttokohde),
                    request.maaraM3 ? `${request.maaraM3} m³` : null,
                    t("valut.listCard.sentPrefix", { date: formatSentDate(request.sentAt || request.createdAt, lang) }),
                ].filter(Boolean).join(" · ")}
            </Typography>

            {request.status !== "expired" && request.status !== "cancelled" && (
                <OfferProgressIndicator currentStep={request.status === "accepted" ? 3 : 2} />
            )}

            <FrostedCard sx={{ p: 2, mb: 3 }}>
                <Stack
                    direction="row"
                    onClick={() => setRecapOpen((o) => !o)}
                    role="button"
                    tabIndex={0}
                    aria-expanded={recapOpen}
                    aria-label={t("valut.detail.recapHeading")}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setRecapOpen((o) => !o);
                        }
                    }}
                    sx={{
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: recapOpen ? 1 : 0,
                        cursor: "pointer",
                        userSelect: "none",
                        "&:focus-visible": { outline: (t) => `2px solid ${t.palette.primary.main}`, outlineOffset: 2, borderRadius: 1 },
                    }}
                >
                    <Typography variant="subtitle2">{t("valut.detail.recapHeading")}</Typography>
                    <ExpandMoreIcon sx={{ transform: recapOpen ? "rotate(180deg)" : "none", transition: "transform 120ms", color: "text.secondary" }} />
                </Stack>
                <Collapse in={recapOpen}>
                    <Stack spacing={0.75} sx={{ mt: 1 }}>
                        <SpecRow label={t("valut.detail.addressLabel")} value={request.formattedAddress || request.osoite} />
                        <SpecRow label={t("valut.detail.kayttokohdeLabel")} value={labelFor(kkOptions, request.kayttokohde, request.kayttokohde)} />
                        <SpecRow label={t("valut.detail.amountLabel")} value={request.maaraM3 ? `${request.maaraM3} m³` : ""} />
                        <SpecRow label={t("valut.detail.rasitusluokkaLabel")} value={request.rasitusluokka} />
                        <SpecRow label={t("valut.detail.boomLabel")} value={request.puomi === 0 ? t("valut.detail.boomFromRear") : (request.puomi ? `${request.puomi} m` : "")} />
                        <SpecRow label={t("valut.detail.linjaLabel")} value={request.linjanPituus ? `${request.linjanPituus} m` : ""} />
                        <SpecRow label={t("valut.detail.driveLabel")} value={request.ajoOhjeet || ""} />
                        <SpecRow label={t("valut.detail.detailsLabel")} value={request.kuvaus || ""} />
                    </Stack>
                    {person && (
                        <Box sx={{ mt: 2 }}>
                            <AttachmentsSection
                                pumppuRequestId={id}
                                attachments={attachments}
                                onRefetch={refetchAttachments}
                                token={token}
                                ownerAsiakasId={person.ownerAsiakasId}
                                entryByPersonId={person.personId}
                            />
                        </Box>
                    )}
                </Collapse>
            </FrostedCard>

            <Divider sx={{ mb: 3, opacity: 0.4 }} />

            <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
                {t("valut.detail.offersHeading", { count: offers?.length ?? 0 })}
            </Typography>

            <Stack spacing={2}>
                {offers === null && <Typography variant="body2">{t("common.loading")}</Typography>}
                {offers && offers.length === 0 && (
                    <>
                        {request.status === "open" && request.expiresAt && (
                            <Typography variant="subtitle2">
                                {t("valut.detail.validUntil", { date: formatSentDate(request.expiresAt, lang) })}
                            </Typography>
                        )}
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                            {t("valut.detail.waitingForOffers")}
                        </Typography>
                        {person && (
                            <SubscribePushButton
                                promptLabel={t("valut.detail.subscribePromptLabel")}
                                successLabel={t("valut.detail.subscribeSuccessLabel")}
                                helperText={t("valut.detail.subscribeHelperText")}
                            />
                        )}
                    </>
                )}
            </Stack>

            {offers && offers.length > 0 && (
                <Grid container spacing={2}>
                    {offers.map((o) => (
                        <Grid size={{ xs: 12, md: 6, lg: 4 }} key={o.pumppuOfferId}>
                            <OfferCard
                                offer={o}
                                requestStatus={request.status}
                                requestExpiresAt={request.expiresAt}
                                maaraM3={request.maaraM3}
                                onAccept={(offer) => setPendingOffer(offer)}
                                accepting={accepting && pendingOffer?.pumppuOfferId === o.pumppuOfferId}
                                copy={acceptanceCopy}
                            />
                        </Grid>
                    ))}
                </Grid>
            )}

            {viewers.length > 0 && (
                <FrostedCard sx={{ p: 2, mt: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {t("valut.detail.viewersHeading", { count: viewers.length })}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1.5 }}>
                        {t("valut.detail.viewersHelper")}
                    </Typography>
                    <Stack spacing={1.25}>
                        {viewers.map((v, i) => (
                            <Box key={i} sx={{ borderLeft: (t) => `2px solid ${t.palette.divider}`, pl: 1.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{v.asiakasNimi}</Typography>
                                {v.viewedAt && (
                                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                        {formatSentDate(v.viewedAt, lang)}
                                    </Typography>
                                )}
                            </Box>
                        ))}
                    </Stack>
                </FrostedCard>
            )}

            {declinedProviders.length > 0 && (
                <FrostedCard sx={{ p: 2, mt: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {t("valut.detail.declinedHeading", { count: declinedProviders.length })}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1.5 }}>
                        {t("valut.detail.declinedHelper")}
                    </Typography>
                    <Stack spacing={1.25}>
                        {declinedProviders.map((d, i) => (
                            <Box key={i} sx={{ borderLeft: (t) => `2px solid ${t.palette.divider}`, pl: 1.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{d.asiakasNimi}</Typography>
                                {d.reason && (
                                    <Typography variant="body2" sx={{ color: "text.secondary" }}>{d.reason}</Typography>
                                )}
                                {d.declinedAt && (
                                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                        {formatSentDate(d.declinedAt, lang)}
                                    </Typography>
                                )}
                            </Box>
                        ))}
                    </Stack>
                </FrostedCard>
            )}

            <AcceptConfirmModal
                open={!!pendingOffer}
                offer={pendingOffer}
                onClose={() => setPendingOffer(null)}
                onConfirm={handleConfirmAccept}
                submitting={accepting}
                copy={acceptanceCopy}
            />
            {acceptError && (
                <Typography variant="body2" color="error" sx={{ mt: 2 }}>{acceptError}</Typography>
            )}

            <Box sx={{ textAlign: "center", mt: 4 }}>
                <Button component={RouterLink} to="/tilaa" variant="contained" sx={amberCtaSx} size="large">
                    {t("valut.shared.newRequestCta")}
                </Button>
            </Box>

            <Snackbar
                open={!!pushToast}
                autoHideDuration={5000}
                onClose={() => setPushToast(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert severity="success" variant="filled" onClose={() => setPushToast(null)}>
                    {pushToast}
                </Alert>
            </Snackbar>
        </TilaaBackdrop>
    );
}
